import { FC } from 'react';
import { styled } from '@mui/material';
import { Column, Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import { CommitmentType, CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import useCarriedInterestFeeTemplateOptions from 'hooks/lp/data/vehicle/useCarriedInterestFeeTemplateOptions';
import useFeeTemplateOptions from 'hooks/lp/data/fee-manager/useFeeTemplateOptions';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';
import CurrencySelector from 'further-ui/components/CurrencySelector';
import useEqualisationInterestFeeTemplateOptions from 'hooks/lp/data/vehicle/useEqualisationInterestFeeTemplateOptions';

const Container = styled(Column)(({ theme }) => ({
  width: '100%',
  maxWidth: '800px',
  padding: theme.spacing(
    theme.spacingSizes.xs,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
  ),
}));

type Props = {
  vehicleApplicationId: string;
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    commitmentType: CommitmentType;
    carrySettings: {
      carriedInterestFeeTemplates: Array<{
        _id: string;
        name: string;
      }>;
    };
  };
};

const CommitmentStepSettings: FC<Props> = ({
  vehicleApplicationId,
  vehicle,
}) => {
  const lpFeeTemplates = useFeeTemplateOptions(vehicle._id, true);
  const carriedInterestFeeTemplates = useCarriedInterestFeeTemplateOptions(
    vehicle._id,
  );
  const equalisationInterestFeeTemplates =
    useEqualisationInterestFeeTemplateOptions(vehicle._id);
  const { vehicleApplication, saveCommitmentSettings, isPending } =
    useVehicleApplication(vehicle._id, vehicleApplicationId);

  const disableForm = isPending || !!vehicleApplication.commitment;

  const handleTargetCommitmentChange = (value: number | null) => {
    if (disableForm) {
      return;
    }

    saveCommitmentSettings({
      vehicleId: vehicle._id,
      vehicleApplicationId: vehicleApplication.id,
      ...vehicleApplication.commitmentSettings,
      investorLocalCurrency: vehicleApplication.investorLocalCurrency,
      targetCommitment: value ?? 0,
      shouldCompleteCommitment: false,
      shouldNotifyInvestor: false,
    });
  };

  const handleInvestorLocalCurrencyChange = (value: CurrencyCode) => {
    if (disableForm) {
      return;
    }

    saveCommitmentSettings({
      vehicleId: vehicle._id,
      vehicleApplicationId: vehicleApplication.id,
      ...vehicleApplication.commitmentSettings,
      investorLocalCurrency: value,
      targetCommitment: vehicleApplication.targetCommitment,
      shouldCompleteCommitment: false,
      shouldNotifyInvestor: false,
    });
  };

  const handleLpFeeTemplateChange = (value: string) => {
    if (disableForm) {
      return;
    }

    saveCommitmentSettings({
      vehicleId: vehicle._id,
      vehicleApplicationId: vehicleApplication.id,
      investorLocalCurrency: vehicleApplication.investorLocalCurrency,
      targetCommitment: vehicleApplication.targetCommitment,
      ...vehicleApplication.commitmentSettings,
      lpFeeTemplateId: value === 'none' ? undefined : value,
      shouldCompleteCommitment: false,
      shouldNotifyInvestor: false,
    });
  };

  const handleCarriedInterestFeeTemplateChange = (value: string) => {
    if (disableForm) {
      return;
    }

    saveCommitmentSettings({
      vehicleId: vehicle._id,
      vehicleApplicationId: vehicleApplication.id,
      investorLocalCurrency: vehicleApplication.investorLocalCurrency,
      targetCommitment: vehicleApplication.targetCommitment,
      ...vehicleApplication.commitmentSettings,
      carriedInterestFeeTemplateId: value === 'none' ? undefined : value,
      shouldCompleteCommitment: false,
      shouldNotifyInvestor: false,
    });
  };

  const handleEqualisationInterestFeeTemplateChange = (value: string) => {
    if (disableForm) {
      return;
    }

    saveCommitmentSettings({
      vehicleId: vehicle._id,
      vehicleApplicationId: vehicleApplication.id,
      investorLocalCurrency: vehicleApplication.investorLocalCurrency,
      targetCommitment: vehicleApplication.targetCommitment,
      ...vehicleApplication.commitmentSettings,
      equalisationInterestFeeTemplateId: value,
      shouldCompleteCommitment: false,
      shouldNotifyInvestor: false,
    });
  };

  return (
    <Column fullWidth>
      <Container fullWidth spacing="lg">
        <Row fullWidth spacing="sm">
          <FormField label="Target commitment" labelColorLight={disableForm}>
            <NumberInput
              fullWidth
              onBlur={handleTargetCommitmentChange}
              value={vehicleApplication.targetCommitment}
              size="small"
              currency={vehicle.baseCurrency}
              placeholder="Enter target commitment"
              disabled={disableForm}
              decimals={2}
              hideZeroValue
            />
          </FormField>
          <FormField
            label="Investor local currency"
            labelColorLight={disableForm}
          >
            <CurrencySelector
              placeholder="Select currency"
              value={vehicleApplication.investorLocalCurrency}
              onChange={handleInvestorLocalCurrencyChange}
            />
          </FormField>
        </Row>
        <Row fullWidth spacing="sm">
          <FormField
            label="Management fee template"
            labelColorLight={disableForm}
          >
            <BasicSelect
              fullWidth
              size="small"
              placeholder="Select template"
              value={
                vehicleApplication.commitmentSettings.lpFeeTemplateId ?? 'none'
              }
              onChange={handleLpFeeTemplateChange}
              options={lpFeeTemplates}
              disabled={disableForm}
              noOptionsMessage="No fee templates available"
            />
          </FormField>
          <FormField
            label="Carried interest template"
            labelColorLight={disableForm}
          >
            <BasicSelect
              fullWidth
              size="small"
              placeholder="Select template"
              value={
                vehicleApplication.commitmentSettings
                  .carriedInterestFeeTemplateId ?? 'none'
              }
              onChange={handleCarriedInterestFeeTemplateChange}
              options={carriedInterestFeeTemplates}
              disabled={disableForm}
              noOptionsMessage="No fee templates available"
            />
          </FormField>
          <FormField
            label="Equalisation interest fee template"
            labelColorLight={disableForm}
          >
            <BasicSelect
              fullWidth
              size="small"
              placeholder="Select template"
              value={
                vehicleApplication.commitmentSettings
                  .equalisationInterestFeeTemplateId
              }
              onChange={handleEqualisationInterestFeeTemplateChange}
              options={equalisationInterestFeeTemplates}
              disabled={disableForm}
              noOptionsMessage="No fee templates available"
            />
          </FormField>
        </Row>
      </Container>
    </Column>
  );
};

export default CommitmentStepSettings;
